import { AGENT_RESPONSE_TIMEOUT } from "@/configs/constants";
import {
  createContext,
  useContext,
  useRef,
  useCallback,
  PropsWithChildren,
  useEffect,
} from "react";

const AgentTimeoutContext = createContext<{
  handleUserAction: () => void;
  handleAgentAction: () => void;
} | null>(null);

/**
 * Provides a context for handling agent inactivity, exposes methods to
 * register user action and agent action
 * */
export function AgentTimeoutProvider({
  onTimeout,
  timeoutMs = AGENT_RESPONSE_TIMEOUT,
  children,
}: PropsWithChildren & {
  onTimeout: () => void;
  timeoutMs?: number;
}) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleUserAction = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(onTimeout, timeoutMs);

    const handleVisibilityChange = () => {
      if (document.hidden && timeoutRef.current)
        clearTimeout(timeoutRef.current);
    };

    // Add listener for the case of user switching or hiding tab
    document.addEventListener("visibilitychange", handleVisibilityChange, {
      once: true,
    });
  }, [onTimeout, timeoutMs]);

  const handleAgentAction = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <AgentTimeoutContext.Provider
      value={{ handleUserAction, handleAgentAction }}
    >
      {children}
    </AgentTimeoutContext.Provider>
  );
}

export function useAgentTimeout() {
  const ctx = useContext(AgentTimeoutContext);

  if (!ctx)
    throw new Error("useAgentTimeout must be used within AgentTimeoutProvider");

  return ctx;
}
